"use client";

import { useQuery } from "@tanstack/react-query";

export interface MatchEntry {
  market_ticker: string;
  title: string;
  yes_subtitle?: string;
  status?: string;
  similarity?: number;
  explanation?: string;
  // Optional price fields (present on "exact" matches when available)
  price?: number;
  yes_price?: number;
}

export interface MatchingMarketsResult {
  query?: {
    platform?: string;
    condition_id?: string;
    market_slug?: string;
    market_id?: string;
    title?: string;
    status?: string;
  };
  matches?: {
    KALSHI?: {
      exact?: MatchEntry[];
      related?: MatchEntry[];
    };
  };
}

async function fetchMatchingMarkets(conditionId: string): Promise<MatchingMarketsResult | null> {
  if (!conditionId) return null;
  const res = await fetch(`/api/analytics/matching-markets/${conditionId}`);
  if (!res.ok) return null;
  const json = await res.json();
  const data = (json?.data || null) as MatchingMarketsResult | null;
  if (!data) return null;
  // Predexon's classifier emits one row per Kalshi sub-market in a
  // series, so multi-strike series like "$86 or above" appear N times
  // with identical titles. Dedupe by normalized title at the source so
  // every consumer (event hero badge tooltip, sports event panel) sees
  // a clean list. Truly distinct titles survive untouched.
  const dedupeByTitle = (arr?: MatchEntry[]): MatchEntry[] | undefined => {
    if (!Array.isArray(arr)) return arr;
    const seen = new Set<string>();
    return arr.filter((m) => {
      const key = (m.title || m.market_ticker || '').trim().toLowerCase();
      if (!key) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };
  if (data.matches?.KALSHI) {
    data.matches.KALSHI = {
      exact: dedupeByTitle(data.matches.KALSHI.exact),
      related: dedupeByTitle(data.matches.KALSHI.related),
    };
  }
  return data;
}

/**
 * Cross-venue match lookup (Polymarket ↔ Kalshi) via Predexon. Fires once
 * per event detail page. Cached 5 min. Returns null when no matches or
 * Predexon has no LLM classification for this market yet.
 */
export function useMatchingMarkets(conditionId: string | null | undefined, enabled = true) {
  return useQuery<MatchingMarketsResult | null>({
    queryKey: ["analytics", "matching-markets", (conditionId || "").toLowerCase()],
    queryFn: () => fetchMatchingMarkets(conditionId!),
    // Skip under HIDE_PREDEXON — matching-markets is down during the Predexon
    // outage, so the "Also on Kalshi" surfaces hide instead of showing empty.
    enabled: enabled && !!conditionId && process.env.NEXT_PUBLIC_HIDE_PREDEXON_TABS !== "true",
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: false,
  });
}
